import React from 'react';
import { FeaturePageLayout } from '@/components/shared/FeaturePageLayout';
import { Bot } from 'lucide-react';

export default function AiChatAgentPage() {
  return (
    <FeaturePageLayout
      icon={Bot}
      title="AI Chat Agent Setup"
      badge="Core Feature"
      description="Create intelligent, context-aware WhatsApp chat agents that understand your business, handle complex queries, and deliver personalized responses to every customer — all without writing a single line of code."
      capabilities={[
        {
          title: 'Natural Language Understanding',
          description: 'Your AI agent understands customer intent, handles typos, slang, and multi-language queries with ease using advanced NLP.',
        },
        {
          title: 'Custom Personality & Tone',
          description: 'Define whether your agent is formal, friendly, or casual. Set the tone to match your brand voice perfectly.',
        },
        {
          title: 'Context-Aware Conversations',
          description: 'The agent remembers previous messages within a conversation and provides coherent, contextual follow-ups.',
        },
        {
          title: 'Multi-Language Support',
          description: 'Serve customers in English, Hindi, and regional languages. The AI detects the language automatically and responds accordingly.',
        },
        {
          title: 'Smart Fallback & Escalation',
          description: 'When the AI is unsure, it gracefully asks for clarification or routes the conversation to a human agent.',
        },
        {
          title: 'Instant Activation',
          description: 'Your AI agent goes live on WhatsApp within minutes of configuration. No deployment pipeline or technical setup needed.',
        },
      ]}
      howItWorks={[
        {
          title: 'Name & Configure Your Agent',
          description: 'Give your agent a name, select a personality preset or customize from scratch, and define how it should greet customers.',
        },
        {
          title: 'Connect Your WhatsApp Number',
          description: 'Link your existing WhatsApp number — no Business API needed. Our open-source integration connects in seconds.',
        },
        {
          title: 'Set Conversation Rules',
          description: 'Define response boundaries, escalation triggers, and working hours. Your agent respects your business logic.',
        },
        {
          title: 'Go Live & Monitor',
          description: 'Activate the agent and start handling real conversations. Monitor performance from your dashboard in real-time.',
        },
      ]}
      useCases={[
        { emoji: '🏪', title: 'Retail Stores', description: 'Answer product queries, check stock availability, and share pricing with walk-in and online customers.' },
        { emoji: '🍽️', title: 'Restaurants', description: 'Handle menu inquiries, table reservations, and delivery orders through WhatsApp conversations.' },
        { emoji: '🏥', title: 'Clinics & Healthcare', description: 'Schedule appointments, answer FAQs about services, and send reminders automatically.' },
        { emoji: '🎓', title: 'Education Institutes', description: 'Respond to admission queries, share course details, and handle enrollment inquiries.' },
        { emoji: '🏠', title: 'Real Estate', description: 'Share property details, schedule visits, and qualify leads through conversational AI.' },
        { emoji: '💼', title: 'Service Businesses', description: 'Automate booking, share quotes, and handle customer inquiries 24/7 on WhatsApp.' },
      ]}
      ctaText="Launch your AI agent in under 5 minutes"
    />
  );
}
